"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useState } from "react";
import { AUTONOMY_PRACTITIONER_NPIS, buildTeReasonContext, generateTeMessage, generateTeReasonText, getMissingInformationSummary, hasAutonomyDiagnosisRules, hasSleepDiagnosis, isOptumServeReferral, requiresHmoReferral } from "@/configuration/groups/autonomy/configuration";
import { AUTONOMY_REFERRAL_MAPPING } from "@/configuration/groups/autonomy/document-mappings";
import { AUTONOMY_PRACTITIONER_MAPPINGS } from "@/configuration/groups/autonomy/generated/practitioner-mappings";
import { formatAutonomyRenderingMismatch } from "@/configuration/groups/autonomy/referral-chart-enrichment";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { PDFViewer } from "@/src/components/pdf-viewer/pdf-viewer";
import type { AutonomyReferralAssignmentFact, AutonomyReferralChartResult, AutonomyReferralEmailFact, AutonomyReferralEnrichmentFact, AutonomyReferralInstructionsPresentation, AutonomyReferralOtherReferralOutcome, AutonomyReferralTelephoneEncounterDraftFact, AutonomyReferralTelephoneEncounterFact, AutonomyReferralUploadFact } from "@/src/components/server/autonomy-referral-instructions-step-output";
import { Alert, AlertDescription } from "@/src/components/shadcn/ui/alert";
import { Button } from "@/src/components/shadcn/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/src/components/shadcn/ui/dialog";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { useProvidersForDiagnostics } from "@/src/lib/data-fetchers/diagnosis-providers-fetchers";
import { fileViewUrl } from "@/src/lib/files/file-view-url";
import { formatDateForFilename, formatDateMmDdYyyy } from "@/src/lib/utils/date-and-phone-utils";
import { type WebecwTelephoneEncounterIdentity, WebecwTelEncNonCreationReason } from "@/src/lib/workflows/steps/webecw/tel-enc-contract";
import { V2WorkflowType } from "@/src/models/constants/workflow-types";
import { isStatOrUrgentPriority, Referral } from "@/src/models/documents/referral";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";
import { Task, TaskSource, TaskStatus } from "@/src/models/task";
const AUTONOMY_PRACTITIONER_NPI_BY_EHR_ID: Record<string, string> = Object.fromEntries(Object.entries(AUTONOMY_PRACTITIONER_NPIS).map(([npi, info]) => [info.ehrId, npi]));
interface AutonomyReferralInstructionsProps {
  referral: Referral;
  task: Task;
  faxSenderNumber?: string | null;
  presentation: AutonomyReferralInstructionsPresentation;
}
type AutonomyReferralEhrPresentation = Exclude<AutonomyReferralInstructionsPresentation, {
  mode: V2WorkflowType.REFERRAL_EMAIL_ONLY_WF;
}>;
type AutonomyReferralMessageProps = Omit<AutonomyReferralInstructionsProps, "presentation"> & {
  presentation: AutonomyReferralEhrPresentation;
};
const AUTONOMY_ECW_URL = "https://gacent96bbda5pqn16app.ecwcloud.com/mobiledoc/jsp/webemr/index.jsp";
const AUTONOMY_TE_NON_CREATION_FACTS: Record<WebecwTelEncNonCreationReason, string> = {
  [WebecwTelEncNonCreationReason.NOT_NEEDED]: "Telephone Encounter creation was not needed",
  [WebecwTelEncNonCreationReason.UNKNOWN_CHART_ORIGIN]: "Chart origin could not be confirmed",
  [WebecwTelEncNonCreationReason.EXISTING_TE]: "An existing Telephone Encounter was found",
  [WebecwTelEncNonCreationReason.OTHER_REFERRAL]: "Another referral for this patient holds the Telephone Encounter",
  [WebecwTelEncNonCreationReason.GUARD_UNAVAILABLE]: "Encounter history could not be confirmed",
  [WebecwTelEncNonCreationReason.UNRESOLVED_ASSIGNEE]: "Staff assignee could not be resolved",
  [WebecwTelEncNonCreationReason.UNRESOLVED_DOCTOR]: "Telephone Encounter doctor could not be resolved",
  [WebecwTelEncNonCreationReason.UNRESOLVED_FACILITY]: "Telephone Encounter facility could not be resolved",
  [WebecwTelEncNonCreationReason.EXPLICIT_CREATE_FAILURE]: "eCW reported that Telephone Encounter creation failed",
  [WebecwTelEncNonCreationReason.UNCONFIRMED_CREATE_RESULT]: "Telephone Encounter creation result could not be confirmed",
  [WebecwTelEncNonCreationReason.AUTHORIZATION_ON_EXISTING_TE]: "The referral authorization is on the patient's open Telephone Encounter for the PCP referral",
  [WebecwTelEncNonCreationReason.MULTIPLE_APPEND_TARGETS]: "More than one open Telephone Encounter mentions a PCP referral in Action Taken",
  [WebecwTelEncNonCreationReason.APPEND_UNCONFIRMED]: "The referral authorization could not be confirmed on the open Telephone Encounter for the PCP referral"
};
function findOutboundFaxFile(task: Task) {
  return task.files.find(file => file.source === TaskSource.FAX_OUTBOUND);
}
function getProviderName(provider: Practitioner | null, fallback: string): string {
  if (provider) return getPractitionerNameWithCredentials(provider) || fallback;else return fallback;
}
function AssignmentNote() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="text-xs text-muted-foreground italic">Note: Verify assignment based on patient's last encounter provider</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function ManualUploadBoxes(t0) {
  const $ = _c(19);
  const {
    fileName,
    assigneeName,
    showAssignmentNote
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="font-medium">Document Type:</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div>{t1}{" "}<CopyableContent text={AUTONOMY_REFERRAL_MAPPING.displayName} /></div>;
    t3 = <span className="font-medium">ECW Folder:</span>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div>{t3}{" "}<CopyableContent text={AUTONOMY_REFERRAL_MAPPING.folderMapping} /></div>;
    t5 = <span className="font-medium">File name:</span>;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== fileName) {
    t6 = <div>{t5} <CopyableContent text={fileName} /></div>;
    $[5] = fileName;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="font-medium">Assign to (receiving provider):</span>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== assigneeName) {
    t8 = <div>{t7} <CopyableContent text={assigneeName} /></div>;
    $[8] = assigneeName;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== showAssignmentNote) {
    t9 = showAssignmentNote && <AssignmentNote />;
    $[10] = showAssignmentNote;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t8 || $[13] !== t9) {
    t10 = <div className="space-y-1">{t8}{t9}</div>;
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="text-xs text-muted-foreground italic">{AUTONOMY_REFERRAL_MAPPING.markAsReviewed ? "Mark the document as reviewed." : "Leave the document open."}</div>;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t6) {
    t12 = <div className="space-y-2">{t2}{t4}{t6}{t10}{t11}</div>;
    $[16] = t10;
    $[17] = t6;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  return t12;
}
function ManualTelephoneEncounterBoxes(t0) {
  const $ = _c(26);
  const {
    assigneeName,
    showAssignmentNote,
    reason,
    showReasonPrefixNote,
    message,
    highPriority
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="font-medium">Assignment (receiving provider)</div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== assigneeName) {
    t2 = <div><CopyableContent text={assigneeName} /></div>;
    $[1] = assigneeName;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== showAssignmentNote) {
    t3 = showAssignmentNote && <AssignmentNote />;
    $[3] = showAssignmentNote;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <div>{t1}<div className="ml-2 text-sm space-y-1">{t2}{t3}</div></div>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="font-medium">Reason</div>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== reason) {
    t6 = <div className="ml-2 bg-muted/50 p-2 rounded"><CopyableContent text={reason} /></div>;
    $[9] = reason;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== showReasonPrefixNote) {
    t7 = showReasonPrefixNote && <div className="ml-2 mt-1 text-xs text-muted-foreground italic">Add NP (New Patient) or EP (Established Patient) prefix based on encounter history</div>;
    $[11] = showReasonPrefixNote;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <div>{t5}{t6}{t7}</div>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="font-medium">Message</div>;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== message) {
    t10 = <div>{t9}<div className="ml-2 bg-muted/50 p-3 rounded"><div className="text-sm whitespace-pre-wrap font-mono">{message}</div></div></div>;
    $[17] = message;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== highPriority) {
    t11 = highPriority && <div><span className="font-medium">Priority:</span> High</div>;
    $[19] = highPriority;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t4 || $[24] !== t8) {
    t12 = <div className="space-y-3">{t4}{t8}{t10}{t11}</div>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t4;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
}
function getResolvedAssigneeName(assignment: AutonomyReferralAssignmentFact): string | null {
  return assignment.available && assignment.resolved ? assignment.name : null;
}
function getTelephoneEncounterBoxValues(draft: AutonomyReferralTelephoneEncounterDraftFact, fallbackReason: string, fallbackMessage: string): {
  reason: string;
  message: string;
  showReasonPrefixNote: boolean;
} {
  return draft.available ? {
    reason: draft.reason,
    message: draft.message,
    showReasonPrefixNote: false
  } : {
    reason: fallbackReason,
    message: fallbackMessage,
    showReasonPrefixNote: true
  };
}
function getChartResultText(chart: AutonomyReferralChartResult): string {
  return chart.created ? "Patient chart created in eCW" : "Existing patient chart updated in eCW";
}
function AutonomyReferralProviderReview(t0) {
  const $ = _c(7);
  const {
    enrichment
  } = t0;
  if (!enrichment.available) {
    return null;
  }
  let t1;
  if ($[0] !== enrichment.providerVerificationRequired) {
    t1 = enrichment.providerVerificationRequired && <Alert className="mt-2"><AlertDescription className="text-sm">Provider enrichment requires review; verify the referring and rendering provider fields in eCW.</AlertDescription></Alert>;
    $[0] = enrichment.providerVerificationRequired;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== enrichment.renderingMismatch) {
    t2 = enrichment.renderingMismatch && <Alert className="mt-2"><AlertDescription className="text-sm">⚠️ {formatAutonomyRenderingMismatch(enrichment.renderingMismatch)}</AlertDescription></Alert>;
    $[2] = enrichment.renderingMismatch;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function AutonomyReferralUploadResult(t0) {
  const $ = _c(5);
  const {
    assignment,
    upload
  } = t0;
  if (!upload.available) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <>Document upload result unavailable</>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (!assignment.available) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <>Document uploaded; assignment result unavailable</>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (!assignment.resolved) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <>The document was uploaded without the intended Staff assignment; assign it to the correct reviewer in eCW.</>;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== assignment.name) {
    t1 = <>Uploaded and assigned to <CopyableContent text={assignment.name} /></>;
    $[3] = assignment.name;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function AutonomyReferralEmailMessage(t0) {
  const $ = _c(3);
  const {
    email
  } = t0;
  const result = !email.available ? "Email forwarding result unavailable" : email.sent ? "Referral forwarded by email" : "Referral email was not sent \u2014 route the referral manually";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="font-medium text-center mb-4">Autonomy Health Referral Instructions:</p>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== result) {
    t2 = <div className="flex flex-col gap-1">{t1}<div className="text-muted-foreground">{result}</div></div>;
    $[1] = result;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function TelephoneEncounterIdentityText(t0) {
  const $ = _c(5);
  const {
    telephoneEncounter
  } = t0;
  let t1;
  if ($[0] !== telephoneEncounter.date) {
    t1 = formatDateMmDdYyyy(telephoneEncounter.date);
    $[0] = telephoneEncounter.date;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const date = t1;
  const t2 = date ? ` dated ${date}` : "";
  let t3;
  if ($[2] !== t2 || $[3] !== telephoneEncounter.reason) {
    t3 = <>{t2}, with the Reason “{telephoneEncounter.reason}”</>;
    $[2] = t2;
    $[3] = telephoneEncounter.reason;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function AuthorizationLineBox(t0) {
  const $ = _c(2);
  const {
    authorizationLine
  } = t0;
  if (!authorizationLine) {
    return null;
  }
  let t1;
  if ($[0] !== authorizationLine) {
    t1 = <div className="bg-muted/50 p-2 rounded"><CopyableContent text={authorizationLine} /></div>;
    $[0] = authorizationLine;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function OtherReferralTelephoneEncounterAction(t0) {
  const $ = _c(15);
  const {
    outcome,
    authorizationLine
  } = t0;
  const {
    otherReferral,
    telephoneEncounter
  } = outcome;
  const t1 = `/tasks/${otherReferral.displayId}`;
  let t2;
  if ($[0] !== otherReferral.displayId || $[1] !== t1) {
    t2 = <Link href={t1} className="font-medium text-foreground underline">{otherReferral.displayId}</Link>;
    $[0] = otherReferral.displayId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const otherReferralLink = t2;
  let t3;
  if ($[3] !== otherReferralLink || $[4] !== telephoneEncounter) {
    t3 = telephoneEncounter ? <div>{otherReferralLink} has this patient's Telephone Encounter<TelephoneEncounterIdentityText telephoneEncounter={telephoneEncounter} />.</div> : <div>{otherReferralLink} arrived first and was still being processed, so the Telephone Encounter is left to it.</div>;
    $[3] = otherReferralLink;
    $[4] = telephoneEncounter;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="font-medium">Action:</span>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== otherReferralLink) {
    t5 = <div>{t4} Add anything new from this referral to that Telephone Encounter instead of creating another. If {otherReferralLink} has no Telephone Encounter, create one from its card.</div>;
    $[7] = otherReferralLink;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== authorizationLine) {
    t6 = authorizationLine && <><div>Add this referral's authorization to that Telephone Encounter's Messages if it isn't there:</div><AuthorizationLineBox authorizationLine={authorizationLine} /></>;
    $[9] = authorizationLine;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t3 || $[12] !== t5 || $[13] !== t6) {
    t7 = <>{t3}{t5}{t6}</>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
function AutonomyReferralTelephoneEncounterResult(t0) {
  const $ = _c(29);
  const {
    telephoneEncounter,
    authorizationLine
  } = t0;
  if (!telephoneEncounter.available) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <>Telephone Encounter automation result is unavailable</>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (telephoneEncounter.outcome.created) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <>eCW confirmed the Telephone Encounter was created</>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const outcome = telephoneEncounter.outcome;
  const t1 = AUTONOMY_TE_NON_CREATION_FACTS[outcome.reason];
  let t2;
  if ($[2] !== t1) {
    t2 = <div>{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== outcome.reason || $[5] !== outcome.telephoneEncounter) {
    t3 = (outcome.reason === WebecwTelEncNonCreationReason.AUTHORIZATION_ON_EXISTING_TE || outcome.reason === WebecwTelEncNonCreationReason.APPEND_UNCONFIRMED) && <div>That is the Telephone Encounter<TelephoneEncounterIdentityText telephoneEncounter={outcome.telephoneEncounter} />.</div>;
    $[4] = outcome.reason;
    $[5] = outcome.telephoneEncounter;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== authorizationLine || $[8] !== telephoneEncounter.outcome || $[9] !== telephoneEncounter.reviewAction) {
    t4 = telephoneEncounter.reviewAction === WebecwTelEncNonCreationReason.OTHER_REFERRAL && <OtherReferralTelephoneEncounterAction outcome={telephoneEncounter.outcome} authorizationLine={authorizationLine} />;
    $[7] = authorizationLine;
    $[8] = telephoneEncounter.outcome;
    $[9] = telephoneEncounter.reviewAction;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== telephoneEncounter.reviewAction) {
    t5 = telephoneEncounter.reviewAction === WebecwTelEncNonCreationReason.EXISTING_TE && <div><span className="font-medium">Action:</span> Check that Telephone Encounter before creating another.</div>;
    $[11] = telephoneEncounter.reviewAction;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== telephoneEncounter.reviewAction) {
    t6 = telephoneEncounter.reviewAction === WebecwTelEncNonCreationReason.UNCONFIRMED_CREATE_RESULT && <div><span className="font-medium">Action:</span> Verify in eCW whether the Telephone Encounter already exists before creating one.</div>;
    $[13] = telephoneEncounter.reviewAction;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== authorizationLine || $[16] !== telephoneEncounter.reviewAction) {
    t7 = telephoneEncounter.reviewAction === WebecwTelEncNonCreationReason.MULTIPLE_APPEND_TARGETS && <><div><span className="font-medium">Action:</span> Add this line to the Messages of the Telephone Encounter for the PCP referral, keeping its owner:</div><AuthorizationLineBox authorizationLine={authorizationLine} /></>;
    $[15] = authorizationLine;
    $[16] = telephoneEncounter.reviewAction;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== authorizationLine || $[19] !== telephoneEncounter.reviewAction) {
    t8 = telephoneEncounter.reviewAction === WebecwTelEncNonCreationReason.APPEND_UNCONFIRMED && <><div><span className="font-medium">Action:</span> Check that Telephone Encounter's Messages and add this line if it isn't there, keeping its owner:</div><AuthorizationLineBox authorizationLine={authorizationLine} /></>;
    $[18] = authorizationLine;
    $[19] = telephoneEncounter.reviewAction;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t2 || $[22] !== t3 || $[23] !== t4 || $[24] !== t5 || $[25] !== t6 || $[26] !== t7 || $[27] !== t8) {
    t9 = <div className="space-y-2">{t2}{t3}{t4}{t5}{t6}{t7}{t8}</div>;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
}
function AutonomyReferralMessage(t0) {
  const $ = _c(59);
  const {
    referral,
    task,
    faxSenderNumber: t1,
    presentation
  } = t0;
  const faxSenderNumber = t1 === undefined ? null : t1;
  const teContext = buildTeReasonContext(referral, task, null);
  const teReason = generateTeReasonText(teContext);
  const teMessage = generateTeMessage(referral, task, faxSenderNumber);
  const missingInfo = getMissingInformationSummary(referral, task);
  let t2;
  if ($[0] !== referral) {
    t2 = requiresHmoReferral(referral);
    $[0] = referral;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const needsHmoReferral = t2;
  let t3;
  if ($[2] !== referral.schedulingPriority) {
    t3 = isStatOrUrgentPriority(referral.schedulingPriority);
    $[2] = referral.schedulingPriority;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const isUrgent = t3;
  let t4;
  if ($[4] !== referral) {
    t4 = hasSleepDiagnosis(referral);
    $[4] = referral;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const isSleepDiagnosis = t4;
  let t5;
  if ($[6] !== faxSenderNumber || $[7] !== referral) {
    t5 = isOptumServeReferral(faxSenderNumber, referral);
    $[6] = faxSenderNumber;
    $[7] = referral;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const isOptumServe = t5;
  const {
    data: matchedProviders
  } = useProvidersForDiagnostics(referral.diagnosisList ?? null);
  const outboundFaxFile = findOutboundFaxFile(task);
  const [dialogOpen, setDialogOpen] = useState(false);
  let t6;
  if ($[9] !== referral.dateReceived) {
    t6 = formatDateForFilename(referral.dateReceived, "");
    $[9] = referral.dateReceived;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== referral.referringProvider) {
    t7 = getProviderName(referral.referringProvider, "Unknown Provider");
    $[11] = referral.referringProvider;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const fileName = `${t6} Referral from ${t7}`;
  let t8;
  if ($[13] !== referral.receivingProvider) {
    t8 = getProviderName(referral.receivingProvider, "Rendering Provider");
    $[13] = referral.receivingProvider;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const renderingProviderName = t8;
  const npi = referral.receivingProvider?.npiNumber ?? null;
  const supervisorLeadMa = (npi ? AUTONOMY_PRACTITIONER_MAPPINGS[npi] : null)?.supervisorLeadMa || null;
  const staffName = isSleepDiagnosis ? "Nicole Dunlap" : supervisorLeadMa;
  const manualAssigneeName = staffName ?? `${renderingProviderName} - Staff`;
  let t9;
  if ($[15] !== matchedProviders) {
    t9 = new Set((matchedProviders ?? []).map(_temp).filter(_temp2));
    $[15] = matchedProviders;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const matchedNpis = t9;
  let t10;
  if ($[17] !== isSleepDiagnosis || $[18] !== matchedNpis || $[19] !== matchedProviders?.length || $[20] !== npi) {
    t10 = !isSleepDiagnosis && hasAutonomyDiagnosisRules(npi) && !!matchedProviders?.length && !matchedNpis.has(npi);
    $[17] = isSleepDiagnosis;
    $[18] = matchedNpis;
    $[19] = matchedProviders?.length;
    $[20] = npi;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const hasProviderConflict = t10;
  const alternativeProviderNames = (matchedProviders ?? []).map(_temp3);
  const t11 = "flex flex-col gap-1";
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <p className="font-medium text-center mb-4">Autonomy Health Referral Instructions:</p>;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = "text-muted-foreground flex flex-col gap-2";
  let t14;
  if ($[23] !== fileName || $[24] !== manualAssigneeName || $[25] !== presentation.assignment || $[26] !== presentation.chart || $[27] !== presentation.documentLabel || $[28] !== presentation.enrichment || $[29] !== presentation.mode || $[30] !== presentation.upload) {
    t14 = presentation.mode === V2WorkflowType.INBOUND_REFERRAL_WF ? <><div className="flex flex-col gap-2"><span className="font-semibold">1. {getChartResultText(presentation.chart)}:{" "}<a href={AUTONOMY_ECW_URL} target="_blank" rel="noopener noreferrer" className="hover:bg-accent rounded p-1">Link</a></span><div className="ml-4 space-y-1 text-xs"><div>Complete: Pharmacy, Additional Information, Responsible Party sections</div><div>Run insurance eligibility if plan type is unclear</div></div></div><AutonomyReferralProviderReview enrichment={presentation.enrichment} /><div className="flex flex-col gap-2"><span className="font-semibold">2. Document upload</span><div className="ml-4 space-y-2"><AutonomyReferralUploadResult assignment={presentation.assignment} upload={presentation.upload} />{!presentation.upload.available && <><div>Check {AUTONOMY_REFERRAL_MAPPING.folderMapping} for this document before uploading it.</div><ManualUploadBoxes fileName={presentation.documentLabel ?? fileName} assigneeName={getResolvedAssigneeName(presentation.assignment) ?? manualAssigneeName} showAssignmentNote={!getResolvedAssigneeName(presentation.assignment)} /></>}</div></div></> : <><div className="flex flex-col gap-2"><span className="font-semibold">1. Create or Update the patient in eCW:{" "}<a href={AUTONOMY_ECW_URL} target="_blank" rel="noopener noreferrer" className="hover:bg-accent rounded p-1">Link</a></span><div className="ml-4 space-y-1 text-xs"><div>Complete: Pharmacy, Additional Information, Responsible Party sections</div><div>Run insurance eligibility if plan type is unclear</div></div></div><div className="flex flex-col gap-2"><span className="font-semibold">2. Upload the file to the proper folder</span><div className="ml-4"><ManualUploadBoxes fileName={fileName} assigneeName={manualAssigneeName} showAssignmentNote={true} /></div></div></>;
    $[23] = fileName;
    $[24] = manualAssigneeName;
    $[25] = presentation.assignment;
    $[26] = presentation.chart;
    $[27] = presentation.documentLabel;
    $[28] = presentation.enrichment;
    $[29] = presentation.mode;
    $[30] = presentation.upload;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span className="font-semibold">3. Telephone Encounter</span>;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const t16 = presentation.mode === V2WorkflowType.INBOUND_REFERRAL_WF ? <div className="ml-4 space-y-3"><AutonomyReferralTelephoneEncounterResult telephoneEncounter={presentation.telephoneEncounter} authorizationLine={presentation.authorizationLine} />{presentation.telephoneEncounterBoxes.show && <ManualTelephoneEncounterBoxes assigneeName={getResolvedAssigneeName(presentation.assignment) ?? manualAssigneeName} showAssignmentNote={!getResolvedAssigneeName(presentation.assignment)} highPriority={presentation.highPriority} {...getTelephoneEncounterBoxValues(presentation.telephoneEncounterBoxes.draft, teReason, teMessage)} />}</div> : <div className="ml-4"><ManualTelephoneEncounterBoxes assigneeName={manualAssigneeName} showAssignmentNote={true} reason={teReason} showReasonPrefixNote={true} message={teMessage} highPriority={presentation.highPriority} /></div>;
  let t17;
  if ($[33] !== t16) {
    t17 = <div className="flex flex-col gap-2">{t15}{t16}</div>;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== isUrgent || $[36] !== referral.schedulingPriority) {
    t18 = isUrgent && <Alert className="mt-2"><AlertDescription className="text-sm">⚠️ Scheduling priority is {referral.schedulingPriority}. Mark the Telephone Encounter as High Priority in eCW.</AlertDescription></Alert>;
    $[35] = isUrgent;
    $[36] = referral.schedulingPriority;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== isSleepDiagnosis) {
    t19 = isSleepDiagnosis && <Alert className="mt-2"><AlertDescription className="text-sm">Sleep-related diagnosis — Use Eslin Luka as rendering provider.</AlertDescription></Alert>;
    $[38] = isSleepDiagnosis;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== isSleepDiagnosis) {
    t20 = isSleepDiagnosis && <Alert className="mt-2"><AlertDescription className="text-sm">Sleep-related diagnosis — Assign referral document and Telephone Encounter to Nicole Dunlap.</AlertDescription></Alert>;
    $[40] = isSleepDiagnosis;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  const t21 = hasProviderConflict && <Alert className="mt-2"><AlertDescription className="text-sm">⚠️ {renderingProviderName} doesn't typically accept this diagnosis per the scheduling guide. Consider: {alternativeProviderNames.join(", ")}.</AlertDescription></Alert>;
  let t22;
  if ($[42] !== needsHmoReferral) {
    t22 = needsHmoReferral && <Alert className="mt-2"><AlertDescription className="text-sm">⚠️ Patient Needs PCP/HMO Referral. Please contact referring provider.</AlertDescription></Alert>;
    $[42] = needsHmoReferral;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== isOptumServe) {
    t23 = isOptumServe && <Alert className="mt-2"><AlertDescription className="text-sm">Optum Serve referral; only Gincy Samuel, MD accepts these referrals</AlertDescription></Alert>;
    $[44] = isOptumServe;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  const t24 = missingInfo.length > 0 && <Alert className="mt-2"><AlertDescription className="text-sm"><div>ℹ️ Requested the following - please check if they've arrived before reaching out to the provider:</div><ul className="list-disc list-inside space-y-1 mt-2">{missingInfo.map(_temp4)}</ul>{outboundFaxFile && <Dialog open={dialogOpen} onOpenChange={setDialogOpen}><DialogTrigger asChild={true}><Button variant="outline" size="sm" className="mt-3">View Outbound Fax</Button></DialogTrigger><DialogContent className="max-w-4xl h-[80vh]"><DialogHeader><DialogTitle>Outbound Fax - Missing Information Request</DialogTitle></DialogHeader><div className="flex-1 overflow-hidden"><PDFViewer url={fileViewUrl(outboundFaxFile.pathToFile)} /></div></DialogContent></Dialog>}</AlertDescription></Alert>;
  let t25;
  if ($[46] !== t14 || $[47] !== t17 || $[48] !== t18 || $[49] !== t19 || $[50] !== t20 || $[51] !== t21 || $[52] !== t22 || $[53] !== t23 || $[54] !== t24) {
    t25 = <div className={t13}>{t14}{t17}{t18}{t19}{t20}{t21}{t22}{t23}{t24}</div>;
    $[46] = t14;
    $[47] = t17;
    $[48] = t18;
    $[49] = t19;
    $[50] = t20;
    $[51] = t21;
    $[52] = t22;
    $[53] = t23;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t12 || $[57] !== t25) {
    t26 = <div className={t11}>{t12}{t25}</div>;
    $[56] = t12;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  return t26;
}
function _temp4(item) {
  return <li key={item}>{item}</li>;
}
function _temp3(p_0) {
  return p_0.name;
}
function _temp2(matched) {
  return !!matched;
}
function _temp(p) {
  return AUTONOMY_PRACTITIONER_NPI_BY_EHR_ID[p.ehrId];
}
function AutonomyReferralCards(t0) {
  const $ = _c(15);
  const {
    referral
  } = t0;
  let t1;
  if ($[0] !== referral.patient) {
    t1 = <PatientCard patient={referral.patient} />;
    $[0] = referral.patient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] !== referral) {
    t2 = <ReferralProviderCards referral={referral} />;
    t3 = <ReferralCard referral={referral} isEditable={true} />;
    $[2] = referral;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== referral.primaryCoverage) {
    t4 = referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable={true} />;
    $[5] = referral.primaryCoverage;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== referral.secondaryCoverage) {
    t5 = referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable={true} />;
    $[7] = referral.secondaryCoverage;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <>{t1}{t2}{t3}{t4}{t5}</>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function AutonomyReferralAutomationRunning() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col gap-1"><p className="font-medium text-center mb-4">Autonomy Health Referral Instructions:</p><div className="text-muted-foreground">Automation is still running for this referral. Refresh once it finishes.</div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function AutonomyReferralInstructions(t0) {
  const $ = _c(12);
  const {
    referral,
    task,
    faxSenderNumber: t1,
    presentation
  } = t0;
  const faxSenderNumber = t1 === undefined ? null : t1;
  let t2;
  if ($[0] !== faxSenderNumber || $[1] !== presentation || $[2] !== referral || $[3] !== task) {
    t2 = task.status === TaskStatus.PROCESSING ? <AutonomyReferralAutomationRunning /> : presentation.mode === V2WorkflowType.REFERRAL_EMAIL_ONLY_WF ? <AutonomyReferralEmailMessage email={presentation.email} /> : <AutonomyReferralMessage referral={referral} task={task} faxSenderNumber={faxSenderNumber} presentation={presentation} />;
    $[0] = faxSenderNumber;
    $[1] = presentation;
    $[2] = referral;
    $[3] = task;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const instructions = t2;
  let t3;
  if ($[5] !== instructions) {
    t3 = <EhrInstructionsCard><div className="text-sm text-muted-foreground">{instructions}</div></EhrInstructionsCard>;
    $[5] = instructions;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== referral) {
    t4 = <AutonomyReferralCards referral={referral} />;
    $[7] = referral;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">{t3}{t4}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}